import classNames from 'classnames';
import * as React from 'react';

import { BreadcrumbButton } from 'bones/design-system/components/breadcrumb/BreadcrumbButton';
import { BreadcrumbDivider } from 'bones/design-system/components/breadcrumb/BreadcrumbDivider';
import {
  BreadcrumbMenu,
  BreadcrumbMenuProps,
} from 'bones/design-system/components/breadcrumb/BreadcrumbMenu';
import { IconComponentType } from 'bones/design-system/components/icons/Icon';
import { MenuItem } from 'bones/design-system/components/select/MenuItem';
import { MenuItems } from 'bones/design-system/components/select/MenuItems';

export interface BreadcrumbItem {
  id: string;
  disabled?: boolean;
  leftIcon?: IconComponentType;
  menuText?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  testId?: string;
  text: string;
}

export interface BreadcrumbProps {
  'data-testid'?: never;
  className?: string;
  items: BreadcrumbItem[];
  menuProps?: Pick<
    BreadcrumbMenuProps,
    | 'boundary'
    | 'className'
    | 'isOpen'
    | 'onInteraction'
    | 'popoverClassName'
    | 'position'
    | 'testId'
  >;
  showFirstItem?: boolean;
}

export const Breadcrumb: React.VFC<BreadcrumbProps> = ({
  className,
  items,
  menuProps,
  showFirstItem = true,
}) => {
  const menuItems = [...items];
  // extract last (current) item before the first item
  const lastItem = menuItems.pop();
  // extract first item before the second to last item
  const firstItem = showFirstItem && menuItems.shift();
  // extract second to last item
  const secondLastItem = menuItems.pop();

  return (
    <div className={classNames(className, 'vb-breadcrumb')}>
      {firstItem ? (
        <>
          <BreadcrumbButton
            disabled={firstItem.disabled}
            leftIcon={firstItem.leftIcon}
            onClick={firstItem.onClick}
            testId={firstItem.testId}
          >
            {firstItem.text}
          </BreadcrumbButton>
          <BreadcrumbDivider />
        </>
      ) : null}

      {menuItems.length ? (
        <>
          <BreadcrumbMenu
            menu={
              <MenuItems>
                {menuItems
                  .slice()
                  .reverse()
                  .map((item) => (
                    <MenuItem
                      key={item.id}
                      disabled={item.disabled}
                      leftIcon={item.leftIcon}
                      onClick={item.onClick}
                      testId={item.testId}
                      text={item.menuText || item.text}
                    />
                  ))}
              </MenuItems>
            }
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...menuProps}
          />
          <BreadcrumbDivider />
        </>
      ) : null}

      {secondLastItem ? (
        <>
          <BreadcrumbButton
            disabled={secondLastItem.disabled}
            leftIcon={secondLastItem.leftIcon}
            onClick={secondLastItem.onClick}
            testId={secondLastItem.testId}
          >
            {secondLastItem.text}
          </BreadcrumbButton>
          <BreadcrumbDivider />
        </>
      ) : null}

      {lastItem ? (
        <>
          <BreadcrumbButton
            active
            disabled={lastItem.disabled}
            leftIcon={lastItem.leftIcon}
            onClick={lastItem.onClick}
            testId={lastItem.testId}
          >
            {lastItem.text}
          </BreadcrumbButton>
        </>
      ) : null}
    </div>
  );
};
